import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["group"];

  connect() {
    console.log("Post children controller connected");

    // Add change listeners to all item checkboxes (those in turbo-frames)
    const itemCheckboxes = this.element.querySelectorAll(
      '[data-group-item] input[type="checkbox"]',
    );
    itemCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => this.checkboxChanged());
    });

    // Add listeners to subgroup header checkboxes
    const subgroupCheckboxes = this.element.querySelectorAll(
      '[data-sub-group-header="true"] input[type="checkbox"]',
    );
    subgroupCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) =>
        this.toggleSubGroupAll(event),
      );
    });

    // Initialize checkbox states
    this.updateAllCheckboxStates();
  }

  // Global "select all" handler
  toggleAll(event) {
    console.log("Toggling ALL checkboxes");
    const isChecked = event.currentTarget.checked;

    // Set all checkboxes in the controller to the same state
    const allCheckboxes = this.element.querySelectorAll(
      'input[type="checkbox"]',
    );
    allCheckboxes.forEach((checkbox) => {
      checkbox.checked = isChecked;
    });
  }

  // Group-level "select all" handler
  toggleGroupAll(event) {
    console.log("Toggling GROUP checkboxes");
    const groupCheckbox = event.currentTarget;
    const isChecked = groupCheckbox.checked;

    // Find the group header and its container
    const groupHeader = groupCheckbox.closest('[data-group-header="true"]');
    if (!groupHeader) return;

    const groupContainer = groupHeader.closest(".flex.flex-col.divide-y");
    if (!groupContainer) return;

    // Select all checkboxes within this group (including subgroup headers and items)
    const groupCheckboxes = groupContainer.querySelectorAll(
      'input[type="checkbox"]',
    );
    groupCheckboxes.forEach((checkbox) => {
      // Skip the group header checkbox itself
      if (checkbox !== groupCheckbox) {
        checkbox.checked = isChecked;
      }
    });

    // Update the main checkbox state
    this.updateMainCheckboxState();
  }

  // Subgroup-level "select all" handler
  toggleSubGroupAll(event) {
    console.log("Toggling SUBGROUP checkboxes");
    const subgroupCheckbox = event.currentTarget;
    const isChecked = subgroupCheckbox.checked;

    // Find the subgroup header
    const subgroupHeader = subgroupCheckbox.closest(
      '[data-sub-group-header="true"]',
    );
    if (!subgroupHeader) return;

    // Get the next sibling div which contains the items
    const itemsContainer = subgroupHeader.nextElementSibling;
    if (!itemsContainer || !itemsContainer.tagName === "DIV") return;

    // Find all checkboxes within this container
    const itemCheckboxes = itemsContainer.querySelectorAll(
      'input[type="checkbox"]',
    );
    itemCheckboxes.forEach((checkbox) => {
      checkbox.checked = isChecked;
    });

    // Update group and main checkbox states
    this.updateGroupCheckboxStates();
    this.updateMainCheckboxState();
  }

  // When any individual checkbox changes
  checkboxChanged() {
    console.log("Individual checkbox changed");
    this.updateAllCheckboxStates();
  }

  // Update all checkbox states from bottom to top
  updateAllCheckboxStates() {
    this.updateSubgroupCheckboxStates();
    this.updateGroupCheckboxStates();
    this.updateMainCheckboxState();
  }

  // Update states of all subgroup header checkboxes
  updateSubgroupCheckboxStates() {
    const subgroupHeaders = this.element.querySelectorAll(
      '[data-sub-group-header="true"]',
    );

    subgroupHeaders.forEach((header) => {
      const subgroupCheckbox = header.querySelector('input[type="checkbox"]');
      if (!subgroupCheckbox) return;

      // Get the next sibling div which contains the items
      const itemsContainer = header.nextElementSibling;
      if (!itemsContainer || !itemsContainer.tagName === "DIV") return;

      // Find all checkboxes within this container
      const itemCheckboxes = itemsContainer.querySelectorAll(
        'input[type="checkbox"]',
      );
      if (itemCheckboxes.length === 0) return;

      // Count checked items
      const checkedCount = Array.from(itemCheckboxes).filter(
        (cb) => cb.checked,
      ).length;

      // Update subgroup checkbox state
      if (checkedCount === 0) {
        subgroupCheckbox.checked = false;
        subgroupCheckbox.indeterminate = false;
      } else if (checkedCount === itemCheckboxes.length) {
        subgroupCheckbox.checked = true;
        subgroupCheckbox.indeterminate = false;
      } else {
        subgroupCheckbox.checked = false;
        subgroupCheckbox.indeterminate = true;
      }
    });
  }

  // Update states of all group header checkboxes
  updateGroupCheckboxStates() {
    const groupHeaders = this.element.querySelectorAll(
      '[data-group-header="true"]',
    );

    groupHeaders.forEach((header) => {
      const groupCheckbox = header.querySelector('input[type="checkbox"]');
      if (!groupCheckbox) return;

      const groupContainer = header.closest(".flex.flex-col.divide-y");
      if (!groupContainer) return;

      // Get all checkboxes within this group except the group header checkbox
      const allGroupCheckboxes = Array.from(
        groupContainer.querySelectorAll('input[type="checkbox"]'),
      ).filter((cb) => cb !== groupCheckbox);

      if (allGroupCheckboxes.length === 0) return;

      // Count checked items
      const checkedCount = allGroupCheckboxes.filter((cb) => cb.checked).length;

      // Update group checkbox state
      if (checkedCount === 0) {
        groupCheckbox.checked = false;
        groupCheckbox.indeterminate = false;
      } else if (checkedCount === allGroupCheckboxes.length) {
        groupCheckbox.checked = true;
        groupCheckbox.indeterminate = false;
      } else {
        groupCheckbox.checked = false;
        groupCheckbox.indeterminate = true;
      }
    });
  }

  // Update the state of the global "select all" checkbox
  updateMainCheckboxState() {
    // Find the main checkbox (first one in the parent container, not in a group or subgroup)
    const mainContainer = this.element.closest("#related_content");
    if (!mainContainer) return;

    const mainCheckbox = mainContainer.querySelector(
      '.flex.items-center.justify-between > .flex.items-center.space-x-4 > .w-5 > input[type="checkbox"]',
    );
    if (!mainCheckbox) return;

    // Get all checkboxes except the main one and group/subgroup headers
    const allItemCheckboxes = Array.from(
      this.element.querySelectorAll('[data-group-item] input[type="checkbox"]'),
    );

    if (allItemCheckboxes.length === 0) return;

    // Count checked items
    const checkedCount = allItemCheckboxes.filter((cb) => cb.checked).length;

    // Update main checkbox state
    if (checkedCount === 0) {
      mainCheckbox.checked = false;
      mainCheckbox.indeterminate = false;
    } else if (checkedCount === allItemCheckboxes.length) {
      mainCheckbox.checked = true;
      mainCheckbox.indeterminate = false;
    } else {
      mainCheckbox.checked = false;
      mainCheckbox.indeterminate = true;
    }
  }
}
